import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import GoogleButton, { AuthDivider } from "@/components/auth/GoogleButton";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/feed");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="login-page">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#074572] relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-[#074572] to-[#053a60]" />
        <div className="relative z-10 p-12 text-white max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-11 h-11 rounded-md bg-white/20 flex items-center justify-center p-1">
              <img src="/logo.png" alt="SportsUnity" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>SportsUnity</span>
          </div>
          <h2 className="text-4xl font-medium tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>
            Welcome back to the pitch
          </h2>
          <p className="text-white/70 leading-relaxed">
            Your football network is waiting. Connect with scouts, coaches, and fellow athletes.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-md bg-white border border-[#E6E6E6] flex items-center justify-center p-1">
              <img src="/logo.png" alt="SportsUnity" className="w-full h-full object-contain" />
            </div>
            <span className="text-lg font-semibold text-[#074572]" style={{ fontFamily: 'Outfit' }}>SportsUnity</span>
          </div>

          <h1 className="text-2xl font-medium text-zinc-900 mb-1" style={{ fontFamily: 'Outfit' }}>Sign in</h1>
          <p className="text-sm text-zinc-500 mb-8">Enter your credentials to access your account</p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600" data-testid="login-error">
              {error}
            </div>
          )}

          <GoogleButton label="Sign in with Google" testId="login-google-button" />
          <AuthDivider />

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-zinc-700">Email</Label>
              <Input
                id="email" type="email" placeholder="you@example.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 border-[#E6E6E6] focus:ring-[#074572]/20 focus:border-[#074572]"
                data-testid="login-email-input" required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-700">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password" type={showPw ? "text" : "password"} placeholder="Enter your password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="border-[#E6E6E6] focus:ring-[#074572]/20 focus:border-[#074572] pr-10"
                  data-testid="login-password-input" required
                />
                <button
                  type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  data-testid="login-toggle-password"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button
              type="submit" disabled={loading}
              className="w-full bg-[#F85037] hover:bg-[#e04530] text-white font-medium h-10"
              data-testid="login-submit-button"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <p className="text-sm text-zinc-500 text-center mt-6">
            Don't have an account?{" "}
            <Link to="/signup" className="text-[#074572] font-medium hover:underline" data-testid="login-signup-link">
              Create one
            </Link>
          </p>
          <p className="text-sm text-zinc-500 text-center mt-2">
            <Link to="/forgot-password" className="text-zinc-400 hover:text-[#074572] hover:underline" data-testid="login-forgot-link">
              Forgot password?
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
